'use client'

import { Button } from '@/core/components/form/button/button'
import { useContext } from 'react'
import { createPortal } from 'react-dom'
import { LuChevronLeft, LuChevronRight, LuX } from 'react-icons/lu'

import { cn } from '@/core/utils/common'

import { LayerHeadSlot } from '@/features/veluvit/components/overlay/layer-slot'
import { useSheetNav } from '@/features/veluvit/components/overlay/sheet-nav'
import { closeTop } from '@/features/veluvit/stores/overlay.store'

import type { ButtonHTMLAttributes, ReactNode } from 'react'

/* 모달·시트 안에서 반복되는 조각 — 레퍼런스 .sh-h .x .ds .foot .illu .cond .holds .link */

export function SheetHead({
  title,
  className,
  titleClassName,
  children,
  below,
  closable = true,
}: {
  title: ReactNode
  className?: string
  titleClassName?: string
  children?: ReactNode
  /** 제목 줄 아래, 같은 머리 칸(스크롤 밖)에 붙는 줄 — 탭처럼 본문이 스크롤돼도 늘 위에 있어야 하는 것(채팅 모드 창의 모드 · 모델 탭) */
  below?: ReactNode
  closable?: boolean
}) {
  // 시트 안에서 한 단계 들어온 화면이면 왼쪽에 뒤로 버튼(sheet-nav)
  const nav = useSheetNav()
  const slot = useContext(LayerHeadSlot)
  const row = (
    // 레이어 안이면 판의 머리 칸(스크롤 밖, layer-host)으로 옮겨 그린다 — 본문만 안에서 스크롤되고 머리 아래 선에서 잘린다.
    // 머리를 sticky 로 두고 진한 띠로 덮던 방식은 판과 색이 달라 보였다(2026-10-02). 레이어 밖(슬롯 없음)이면 제자리에 평범하게
    <div
      className={cn(
        'flex min-h-9 items-center justify-between gap-2',
        !slot && !below && 'mb-4',
        className
      )}
    >
      {nav && nav.depth > 0 && (
        <Button
          type="button"
          aria-label="뒤로"
          onClick={nav.pop}
          className="-ml-2 grid size-9 flex-none cursor-pointer place-items-center rounded-full text-t2 transition-colors hover:bg-foreground/[0.07] hover:text-t1 focus-visible:bg-foreground/[0.07] focus-visible:text-t1"
          variant="bare"
        >
          <LuChevronLeft aria-hidden="true" className="size-5.5" />
        </Button>
      )}
      <h3
        className={cn(
          'min-w-0 flex-1 truncate text-c17 font-bold text-t1',
          titleClassName
        )}
      >
        {title}
      </h3>
      {children}
      {closable && <CloseX />}
    </div>
  )
  const head = below ? (
    <div className={cn(!slot && 'mb-4')}>
      {row}
      {below}
    </div>
  ) : (
    row
  )
  return slot ? createPortal(head, slot) : head
}

export function CloseX({ className }: { className?: string }) {
  return (
    <Button
      type="button"
      aria-label="닫기"
      onClick={() => closeTop()}
      className={cn(
        '-mr-2 grid size-9 flex-none cursor-pointer place-items-center rounded-full text-t3 transition-colors hover:bg-foreground/[0.07] hover:text-t1 focus-visible:bg-foreground/[0.07] focus-visible:text-t1',
        className
      )}
      variant="bare"
    >
      <LuX aria-hidden="true" className="size-5" />
    </Button>
  )
}

export function ModalTitle({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <h3
      className={cn(
        'text-center text-c24 leading-[1.35] font-bold text-balance text-t1',
        className
      )}
    >
      {children}
    </h3>
  )
}

export function ModalDesc({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <p
      className={cn(
        'mt-2 text-center text-c14 leading-[1.6] text-pretty text-t2',
        className
      )}
    >
      {children}
    </p>
  )
}

export function Foot({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <p
      className={cn(
        'mt-3 text-center text-c12 leading-[1.55] text-t3',
        className
      )}
    >
      {children}
    </p>
  )
}

export function Cond({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'mt-4 rounded-field bg-foreground/[0.04] px-4 py-3 text-c13 leading-[1.6] text-t2 [&_b]:text-t1',
        className
      )}
    >
      {children}
    </div>
  )
}

/** 보조 링크 — chevron 이면 오른쪽에 › 아이콘을 붙인다(이동하는 링크). 밑줄은 호버 때만 짙어진다 */
export function TextLink({
  children,
  className,
  chevron,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  children: ReactNode
  chevron?: boolean
}) {
  return (
    <Button
      type="button"
      className={cn(
        'inline-flex cursor-pointer items-center gap-0.5 text-c13 text-t3 underline decoration-white/25 underline-offset-4 transition-colors hover:text-t1 hover:decoration-white/60 focus-visible:text-t1',
        className
      )}
      {...rest}
      variant="bare"
    >
      {children}
      {chevron && <LuChevronRight aria-hidden="true" className="size-4" />}
    </Button>
  )
}

/** 모달 머리 아이콘 — 옅은 코랄빛 원 안에 아이콘 하나. 보라 광·반짝이·둥둥 뜨는 연출은 톤에 맞지 않아 뺐다 */
export function Illu({ icon }: { icon: ReactNode }) {
  return (
    <div
      aria-hidden="true"
      className="mx-auto mb-4 grid size-16 place-items-center rounded-full bg-point/12 text-c28 text-point [&_svg]:size-7"
    >
      {icon}
    </div>
  )
}
